import type { AnalyticsEventName, AnalyticsProps } from "@gridaction/core";
import { API_ORIGIN, isNativeApp } from "./platform";
import { sessionToken } from "./session-token";

const ENDPOINT = `${API_ORIGIN}/api/v1/events`;
const SESSION_KEY = "gridaction.visit";
const FLUSH_MS = 5_000;
const MAX_BATCH = 20;

/**
 * A random id for this visit. It groups a person's events together without
 * naming them: it lives in this tab only, is never sent to anyone else, and
 * disappears when the tab does.
 */
function visitId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    // Private windows and blocked storage: the visit just isn't grouped.
    return crypto.randomUUID();
  }
}

interface QueuedEvent {
  name: AnalyticsEventName;
  props: AnalyticsProps;
  sessionId: string;
  at: number;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function send(events: QueuedEvent[], beacon: boolean): void {
  if (events.length === 0) return;
  const body = JSON.stringify({ events });
  // On the way out of the page, a beacon still gets delivered; a fetch may not.
  if (beacon && !isNativeApp && navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
    return;
  }
  const headers: Record<string, string> = { "content-type": "application/json" };
  const token = isNativeApp ? sessionToken() : null;
  if (token) headers.authorization = `Bearer ${token}`;
  void fetch(ENDPOINT, {
    method: "POST",
    headers,
    body,
    credentials: isNativeApp ? "omit" : "include",
    keepalive: true,
    // Measuring must never surface an error to the person using the app.
  }).catch(() => undefined);
}

export function flushEvents(beacon = false): void {
  const pending = queue;
  queue = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  send(pending, beacon);
}

/**
 * Records something the person did. Events are batched for a few seconds so a
 * burst of taps costs one request, and are sent on the way out of the page.
 */
export function trackEvent(name: AnalyticsEventName, props: AnalyticsProps = {}): void {
  queue.push({ name, props, sessionId: visitId(), at: Date.now() });
  if (queue.length >= MAX_BATCH) {
    flushEvents();
    return;
  }
  timer ??= setTimeout(() => flushEvents(), FLUSH_MS);
}

/** A path with ids and codes stripped out, so the same screen groups together. */
export function generalizePath(path: string): string {
  return path
    .replace(/\/[0-9a-f]{8}-[0-9a-f-]{20,}/gi, "/:id")
    .replace(/\/w\/[A-Z0-9]{6,}/gi, "/w/:code")
    .replace(/\/join\/[^/]+/i, "/join/:token")
    .replace(/\?.*$/, "")
    .slice(0, 64);
}

/** Which screen someone is on. */
export function trackPageView(pathname: string): void {
  trackEvent("app.page_viewed", { page: generalizePath(pathname) });
}

let listening = false;

/** Called once at startup: nothing is recorded until this runs. */
export function startAnalytics(): void {
  if (listening) return;
  listening = true;
  addEventListener("pagehide", () => flushEvents(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushEvents(true);
  });
  // The browser tells us when someone adds the app to their home screen.
  addEventListener("appinstalled", () => trackEvent("app.installed", { surface: "web" }));
}
